import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	createFilterOptions,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	LinearProgress,
	Link,
	MenuItem,
	OutlinedInput,
	Skeleton,
	Stack,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { TrashIcon } from "@phosphor-icons/react/dist/ssr/Trash";
import { UploadSimpleIcon } from "@phosphor-icons/react/dist/ssr/UploadSimple";
import { WaveformIcon } from "@phosphor-icons/react/dist/ssr/Waveform";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z as zod } from "zod";

import { apiErrorMessage, formatBytes } from "@/lib/admin-formatters";
import { languageLabel, languageMatches, LANGUAGES } from "@/lib/languages";
import type { ProviderVoice, VoiceProvider } from "@/hooks/api/use-voice-providers";
import { useProviderVoices, useVoiceProviders } from "@/hooks/api/use-voice-providers";
import type { Voice, VoicePayload } from "@/hooks/api/use-voices";
import {
	DEFAULT_SAMPLE_SCRIPT,
	MAX_SAMPLE_SCRIPT_LENGTH,
	MAX_VOICE_SAMPLE_BYTES,
	UPLOAD_PROVIDER_KEY,
	VOICE_SAMPLE_ACCEPT,
} from "@/hooks/api/use-voices";
import { RouterLink } from "@/components/core/link";
import { CustomDrawer } from "@/components/custom-drawer";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { VoiceSampleButton } from "@/components/voice-sample-button";

import { withCurrentProvider } from "./providers";

/** `BCP_47` from `src/modules/campaign-requests/http/requests/patterns.ts`, copied exactly. */
const BCP_47 = /^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/;

/** `PLUGIN_KEY` from `voice.request.ts`: plugin names as `PluginsService` derives them. */
const PLUGIN_KEY = /^[a-z0-9][a-z0-9-]{0,63}$/;

const schema = zod
	.object({
		displayName: zod.string().trim().min(1, "Give it a name").max(120, "120 characters at most"),
		language: zod.string().trim().regex(BCP_47, "A BCP 47 tag — en, ms-MY, zh-Hans-SG"),
		providerPluginKey: zod.string().trim().regex(PLUGIN_KEY, "Choose a provider"),
		providerVoiceId: zod.string().trim().max(255, "255 characters at most"),
		sampleScript: zod.string().trim().max(MAX_SAMPLE_SCRIPT_LENGTH, `${MAX_SAMPLE_SCRIPT_LENGTH} characters at most`),
		gender: zod.string().trim().max(32, "32 characters at most"),
		persona: zod.string().trim().max(160, "160 characters at most"),
		notes: zod.string().trim().max(2000, "2000 characters at most"),
		isActive: zod.boolean(),
	})
	/**
	 * A voice id is required of every provider that issues them, and refused by the one that does
	 * not. Conditional rather than always-required because "Upload" is not a provider with a
	 * missing id — it is the answer for a voice that has no id anywhere in the world.
	 */
	.superRefine((values, ctx) => {
		if (values.providerPluginKey !== UPLOAD_PROVIDER_KEY && !values.providerVoiceId) {
			ctx.addIssue({
				code: zod.ZodIssueCode.custom,
				path: ["providerVoiceId"],
				message: "Choose the voice, or paste its ID",
			});
		}
	});

type FormValues = zod.infer<typeof schema>;

function defaults(voice: Voice | null, language: string): FormValues {
	return {
		displayName: voice?.displayName ?? "",
		language: voice?.language ?? language,
		providerPluginKey: voice?.providerPluginKey ?? "",
		providerVoiceId: voice?.providerVoiceId ?? "",
		// Empty, with the default only shown as a placeholder: the API applies it to a voice
		// created without a script, and only for a real provider. Seeding the box with it would
		// send the line back on every save, including for a recording the API leaves blank.
		sampleScript: voice?.sampleScript ?? "",
		gender: voice?.gender ?? "",
		persona: voice?.persona ?? "",
		notes: voice?.notes ?? "",
		// A new voice starts off the catalogue: its sample is made or uploaded after this form, and
		// a voice nobody can hear is not something to offer a client.
		isActive: voice?.isActive ?? false,
	};
}

/** A voice's own metadata as one muted line: `English · female · american`. */
function voiceDetail(option: ProviderVoice): string {
	return [option.language, option.gender, option.description].filter(Boolean).join(" · ");
}

/**
 * The picker searches everything it shows, and shows at most sixty at a time.
 *
 * ElevenLabs alone answers with several hundred voices, so the list is worth filtering rather
 * than scrolling — and rendering all of them on every keystroke is what makes a picker feel
 * broken. Sixty is more than fits on a screen; anybody who needs the sixty-first types another
 * letter.
 */
const filterVoices = createFilterOptions<ProviderVoice>({
	limit: 60,
	stringify: (option) => [option.name, option.language, option.gender, option.description, option.id].join(" "),
});

export interface VoiceFormDrawerProps {
	open: boolean;
	/** Null adds a voice; a row edits it. */
	voice: Voice | null;
	/** What a new voice's language starts as — the list being looked at, not always English. */
	defaultLanguage: string;
	isSaving: boolean;
	/** The provider is speaking the script right now. */
	isGenerating: boolean;
	/** Why the last generation failed, if it did. Shown here rather than toasted; see below. */
	generateError: unknown;
	onClose: () => void;
	onSave: (payload: VoicePayload, sampleFile: File | null) => void;
	/** Save what is on the form, then have the provider speak the script. */
	onGenerate: (payload: VoicePayload) => void;
	onRemoveSample: (voice: Voice) => void;
}

/**
 * Curating one voice, and giving it something a client can listen to.
 *
 * Everything here except the name and the language is ours: the provider, its voice id and the
 * notes never reach a client, which is the whole point of curating in the first place. What the
 * client reads is the name somebody chose, and what they hear is the sample.
 *
 * The provider is chosen, never typed, and it decides the shape of the rest of the form. A
 * vendor gets a voice picked out of its own catalogue and a script the vendor will speak;
 * "Upload" gets neither, because a voice-to-voice clone is not in anybody's catalogue and there
 * is nothing to synthesise it with — it gets a file, and that is the whole of what it needs. The
 * form asks for what the answer actually is rather than showing every field and greying two.
 *
 * A sample is slow to make and can be refused. Both are said in place: a run states which
 * provider is speaking and that it can take a minute, and a refusal is shown as a refusal, next
 * to the script that caused it. Nothing here ever ends in a player with no sound in it.
 */
export function VoiceFormDrawer({
	open,
	voice,
	defaultLanguage,
	isSaving,
	isGenerating,
	generateError,
	onClose,
	onSave,
	onGenerate,
	onRemoveSample,
}: VoiceFormDrawerProps): React.JSX.Element {
	const { control, handleSubmit, reset, setValue, getValues } = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: defaults(voice, defaultLanguage),
	});

	const nameFieldId = React.useId();
	const voiceIdFieldId = React.useId();
	const genderFieldId = React.useId();
	const personaFieldId = React.useId();

	/** A recording chosen but not sent yet: it uploads with the save, once the row exists. */
	const [sampleFile, setSampleFile] = React.useState<File | null>(null);
	const [fileError, setFileError] = React.useState<string | null>(null);

	/**
	 * The row as it stands now, read without re-seeding the form.
	 *
	 * `voice` is a new object on every refetch — a generated sample, another admin's edit — so an
	 * effect that depended on it would throw away whatever is half-typed each time the catalogue
	 * is re-read. The form is seeded when the drawer opens on a different voice; the sample
	 * section below reads this prop directly, which is how a generation updates in place.
	 */
	const live = React.useRef(voice);
	live.current = voice;

	React.useLayoutEffect(() => {
		// Not on the way out: the drawer animates closed, and re-seeding mid-animation is a blank
		// form flashing over the one somebody just saved.
		if (!open) return;

		reset(defaults(live.current, defaultLanguage));
		setSampleFile(null);
		setFileError(null);
	}, [voice?.id, defaultLanguage, reset, open]);

	const providersQuery = useVoiceProviders(open);
	/**
	 * A failed read is not an empty one. Everything below asks `providersQuery.data` rather than a
	 * default standing in for it, so "no plugin is set up here" is only ever said once the API has
	 * actually said it.
	 */
	const providersFailed = providersQuery.isError;

	const providerKey = useWatch({ control, name: "providerPluginKey" });
	const language = useWatch({ control, name: "language" });
	const script = useWatch({ control, name: "sampleScript" });

	/** Answered, and answered no. Undefined while the read is in flight is not an answer. */
	const noWorkspace = providersQuery.data?.hasWorkspace === false;

	/** Which of the three states a provider missing from the list is in. They are three fixes. */
	const missingReason = providersFailed
		? "The plugins set up here could not be read just now"
		: noWorkspace
			? "Plugins are set up one workspace at a time, and none is selected"
			: "Not set up in this workspace";

	/**
	 * Empty until the list is actually known. Standing the voice's own provider in as "not set up
	 * in this workspace" while the read is still in flight states something false for as long as
	 * it takes, and holds the catalogue read behind it — the fallback it triggers cannot list one.
	 */
	const providers = React.useMemo<VoiceProvider[]>(
		() =>
			providersQuery.data || providersFailed
				? withCurrentProvider(providersQuery.data?.providers ?? [], voice?.providerPluginKey, missingReason)
				: [],
		[providersQuery.data, providersFailed, voice?.providerPluginKey, missingReason]
	);

	const provider = providers.find((option) => option.key === providerKey) ?? null;

	/** A voice that comes from a recording rather than from anybody's catalogue. */
	const isUpload = providerKey === UPLOAD_PROVIDER_KEY;

	/** Whether there is a provider control to show at all, rather than an empty select. */
	const showProvider = providersQuery.isLoading || providers.length > 0;

	/**
	 * One provider is not a choice. It is filled in and stated rather than put behind a select
	 * with a single option — see the read-only line below.
	 */
	React.useEffect(() => {
		if (!open || providerKey || providers.length !== 1) return;
		setValue("providerPluginKey", providers[0].key, { shouldValidate: false });
	}, [open, providerKey, providers, setValue]);

	const voicesQuery = useProviderVoices(provider?.canListVoices ? provider.key : null, open);

	/**
	 * True only while there is nothing to show. A background refetch keeps the list it already
	 * has: `isFetching` here blanks the box somebody is looking at half an hour after they opened
	 * the screen.
	 */
	const catalogueLoading = voicesQuery.isLoading;

	/**
	 * The catalogue could not be read. Not fatal on its own: the id is what the row actually
	 * needs, so this provider falls back to typing it, exactly like one that lists nothing.
	 */
	const catalogueFailed = voicesQuery.isError;

	const toPayload = (values: FormValues): VoicePayload => ({
		displayName: values.displayName.trim(),
		language: values.language.trim(),
		providerPluginKey: values.providerPluginKey.trim(),
		// An uploaded voice has no catalogue id, and an id left behind by a provider somebody
		// changed their mind about is not one either.
		providerVoiceId: values.providerPluginKey === UPLOAD_PROVIDER_KEY ? null : values.providerVoiceId.trim(),
		// Empty is null rather than "", so "no persona recorded" and "somebody typed nothing"
		// are the same state in the column as they are on the screen.
		gender: values.gender.trim() || null,
		persona: values.persona.trim() || null,
		notes: values.notes.trim() || null,
		// An uploaded voice is never shown the script box, so whatever is sitting in it is a
		// default nobody wrote and nothing will ever say. Its row keeps what it already had.
		sampleScript:
			values.providerPluginKey === UPLOAD_PROVIDER_KEY
				? (voice?.sampleScript ?? null)
				: values.sampleScript.trim() || null,
		isActive: values.isActive,
	});

	const submit = handleSubmit((values) => onSave(toPayload(values), sampleFile));

	/**
	 * Generating saves first, and it has to: the sample is stored against a row, and a script the
	 * provider has just spoken that is not the script on the row is a voice whose demo says
	 * something nobody can find. The same handler therefore adds a voice that does not exist yet
	 * — the alternative is telling somebody to save, close, and reopen before they may listen.
	 */
	const generate = handleSubmit((values) => onGenerate(toPayload(values)));

	const chooseFile = (file: File | undefined): void => {
		if (!file) return;

		if (file.size > MAX_VOICE_SAMPLE_BYTES) {
			setFileError(`${file.name} is over the ${formatBytes(MAX_VOICE_SAMPLE_BYTES)} limit`);
			setSampleFile(null);
			return;
		}

		setFileError(null);
		setSampleFile(file);
	};

	/** Nothing to curate against: adding a voice needs a provider, and there is none. */
	const blocked = !voice && (providersQuery.isLoading || providers.length === 0);

	return (
		<CustomDrawer
			open={open}
			onClose={onClose}
			title={voice ? "Edit voice" : "Add a voice"}
			onSubmit={submit}
			isSubmitButtonDisabled={isSaving || isGenerating || blocked}
			submitButtonText={voice ? "Save voice" : "Add voice"}
			width="560px"
		>
			<Grid container spacing={2}>
				<Grid size={12}>
					<Controller
						control={control}
						name="displayName"
						render={({ field, fieldState }) => (
							<FormControl error={Boolean(fieldState.error)} fullWidth required>
								{/* `FormControl` does not wire a label to its input the way `TextField` does,
								    so without these a screen reader announces a bare "edit text". */}
								<InputLabel htmlFor={nameFieldId}>Name</InputLabel>
								<OutlinedInput {...field} id={nameFieldId} label="Name" placeholder="Grace · warm, unhurried" />
								{fieldState.error ? (
									<CustomFormHelperError text={fieldState.error.message || ""} />
								) : (
									<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
										What the client reads. Our words, never the provider&rsquo;s.
									</Typography>
								)}
							</FormControl>
						)}
					/>
				</Grid>

				{/* Nothing configured means nothing to select from: the note below stands in its place
				    rather than an empty dropdown. */}
				{showProvider ? (
					<Grid size={{ xs: 12, sm: 6 }}>
						{providersQuery.isLoading ? (
							<Skeleton height={56} variant="rounded" />
						) : (
							<Controller
								control={control}
								name="providerPluginKey"
								render={({ field, fieldState }) => {
									// One answer is not a question. The provider is stated, not selected.
									if (providers.length === 1) {
										const only = providers[0];
										const why =
											only.key === UPLOAD_PROVIDER_KEY
												? noWorkspace
													? "Choose a workspace to speak a sample — plugins are set up one workspace at a time."
													: "No text-to-speech plugin is set up here, so a voice can only be a recording."
												: `${only.reason ?? "The only text-to-speech plugin set up here"}.`;

										return (
											<Stack spacing={0.25}>
												<Typography variant="caption" color="text.secondary">
													Provider
												</Typography>
												<Typography variant="subtitle2">{only.title}</Typography>
												<Typography variant="caption" color="text.secondary">
													{why}
												</Typography>
											</Stack>
										);
									}

									return (
										<TextField
											{...field}
											select
											fullWidth
											required
											label="Provider"
											error={Boolean(fieldState.error)}
											helperText={fieldState.error?.message ?? "Where this voice comes from."}
											onChange={(event) => {
												field.onChange(event.target.value);
												// A voice id belongs to the provider that issued it, so it goes
												// with the provider it was chosen under.
												setValue("providerVoiceId", "", { shouldValidate: false });
											}}
										>
											{providers.map((option) => (
												<MenuItem key={option.key} value={option.key}>
													{option.title}
												</MenuItem>
											))}
										</TextField>
									);
								}}
							/>
						)}
					</Grid>
				) : null}

				<Grid size={{ xs: 12, sm: showProvider ? 6 : 12 }}>
					<Controller
						control={control}
						name="language"
						render={({ field, fieldState }) => (
							<Autocomplete
								freeSolo
								options={LANGUAGES.map((option) => option.value)}
								// The tag itself, because this is what the box holds and what is sent. A
								// label like "Thai · th" here is written straight back into the field on
								// every programmatic value change — `useAutocomplete` calls
								// `onInputChange` with the label and no `onChange` behind it — and the
								// form is then refused on a field nobody touched.
								getOptionLabel={(option) => option}
								renderOption={(props, option) => {
									const known = LANGUAGES.find((entry) => entry.value === option);
									return (
										<li {...props} key={option}>
											{known ? `${known.label} · ${known.value}` : option}
										</li>
									);
								}}
								value={field.value}
								onChange={(_event, value) => field.onChange(value ?? "")}
								onInputChange={(_event, value) => field.onChange(value)}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Language"
										required
										error={Boolean(fieldState.error)}
										helperText={fieldState.error?.message ?? "One list per language. en also answers en-SG."}
									/>
								)}
							/>
						)}
					/>
				</Grid>

				{providersFailed ? (
					<Grid size={12}>
						<Alert
							severity="error"
							action={
								<Button color="inherit" size="small" onClick={() => void providersQuery.refetch()}>
									Try Again
								</Button>
							}
						>
							The text-to-speech plugins set up here could not be read just now.
						</Alert>
					</Grid>
				) : noWorkspace ? (
					<Grid size={12}>
						{/* `upload` is offered whether or not a workspace is bound, so this is never an empty
						    list — it is a list with one entry and a reason it is the only one. */}
						<Alert severity="info">
							<Stack spacing={1} sx={{ alignItems: "flex-start" }}>
								<Typography variant="body2">
									Plugins are set up one workspace at a time and none is selected, so a voice can only be a recording
									here.
								</Typography>
								<Link component={RouterLink} href="/accounts" variant="body2">
									Choose a workspace
								</Link>
							</Stack>
						</Alert>
					</Grid>
				) : providersQuery.data?.providers.length === 1 ? (
					<Grid size={12}>
						<Alert severity="info">
							<Stack spacing={1} sx={{ alignItems: "flex-start" }}>
								<Typography variant="body2">
									No text-to-speech plugin is set up in this workspace, so there is no provider to build a voice on.
								</Typography>
								<Link component={RouterLink} href="/plugins" variant="body2">
									Set one up in Plugins
								</Link>
							</Stack>
						</Alert>
					</Grid>
				) : null}

				{/* The picker is for choosing a vendor's voice out of a vendor's list. An uploaded
				    voice is in no such list, so the control is not shown rather than shown empty. */}
				{provider && provider.requiresVoiceId ? (
					<Grid size={12}>
						<Controller
							control={control}
							name="providerVoiceId"
							render={({ field, fieldState }) => {
								/**
								 * A typed id, for one provider only and with the reason next to it. Either the
								 * plugin has never synced its catalogue, or it is not set up here any more, or
								 * the read failed — in all three the id is still what the row needs.
								 */
								if (!provider.canListVoices || catalogueFailed) {
									const why = catalogueFailed
										? `${provider.title}’s catalogue could not be read just now.`
										: `${provider.reason ?? "No catalogue to choose from"}.`;

									return (
										<FormControl error={Boolean(fieldState.error)} fullWidth required>
											<InputLabel htmlFor={voiceIdFieldId}>Voice ID</InputLabel>
											<OutlinedInput
												{...field}
												id={voiceIdFieldId}
												label="Voice ID"
												placeholder="21m00Tcm4TlvDq8ikWAM"
											/>
											{fieldState.error ? (
												<CustomFormHelperError text={fieldState.error.message || ""} />
											) : (
												<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
													{why} Paste the ID from {provider.title}.
												</Typography>
											)}
										</FormControl>
									);
								}

								const catalogue = voicesQuery.data ?? [];

								// An id the provider no longer lists still has to show as what it is, rather
								// than as an empty picker over a row that is perfectly valid. Only once the
								// catalogue has actually arrived: mid-load, every id is missing from it.
								const options: ProviderVoice[] =
									field.value && !catalogueLoading && !catalogue.some((option) => option.id === field.value)
										? [
												{
													id: field.value,
													name: field.value,
													language: null,
													gender: null,
													description: "No longer in this provider's list",
												},
												...catalogue,
											]
										: catalogue;

								const selected = options.find((option) => option.id === field.value) ?? null;
								// The same rule the catalogue matches on, so a warning here and an empty
								// step in the wizard can never disagree.
								const mismatched = selected?.language && language && !languageMatches(selected.language, language);

								return (
									<Stack spacing={0.5}>
										<Autocomplete
											options={options}
											value={selected}
											loading={catalogueLoading}
											filterOptions={filterVoices}
											getOptionLabel={(option) => option.name}
											isOptionEqualToValue={(option, value) => option.id === value.id}
											noOptionsText="No voice matches that"
											onChange={(_event, option) => {
												field.onChange(option?.id ?? "");
												// The provider's own answer, where he has not given his own.
												// Never the name: that is his to write.
												if (option?.gender && !getValues("gender")) {
													setValue("gender", option.gender, { shouldValidate: false });
												}
											}}
											renderOption={({ key, ...props }, option) => (
												<Box component="li" key={option.id} {...props}>
													<Stack spacing={0} sx={{ minWidth: 0 }}>
														<Typography variant="body2" noWrap>
															{option.name}
														</Typography>
														{voiceDetail(option) ? (
															<Typography variant="caption" color="text.secondary" noWrap>
																{voiceDetail(option)}
															</Typography>
														) : null}
													</Stack>
												</Box>
											)}
											renderInput={(params) => (
												<TextField
													{...params}
													required
													label="Voice"
													placeholder={catalogueLoading ? "Loading the catalogue…" : "Search by name, accent, gender"}
													error={Boolean(fieldState.error)}
													helperText={fieldState.error?.message ?? undefined}
												/>
											)}
										/>

										{selected ? (
											<Typography variant="caption" color="text.secondary">
												{selected.id}
											</Typography>
										) : null}

										{mismatched ? (
											<Typography variant="caption" color="warning.main">
												{provider.title} lists this voice as {languageLabel(selected!.language)}.
											</Typography>
										) : null}
									</Stack>
								);
							}}
						/>
					</Grid>
				) : null}

				<Grid size={{ xs: 12, sm: 6 }}>
					<Controller
						control={control}
						name="gender"
						render={({ field, fieldState }) => (
							<FormControl error={Boolean(fieldState.error)} fullWidth>
								<InputLabel htmlFor={genderFieldId} shrink>
									Gender
								</InputLabel>
								<OutlinedInput {...field} id={genderFieldId} notched label="Gender" placeholder="female" />
								{fieldState.error ? <CustomFormHelperError text={fieldState.error.message || ""} /> : null}
							</FormControl>
						)}
					/>
				</Grid>

				<Grid size={{ xs: 12, sm: 6 }}>
					<Controller
						control={control}
						name="persona"
						render={({ field, fieldState }) => (
							<FormControl error={Boolean(fieldState.error)} fullWidth>
								<InputLabel htmlFor={personaFieldId} shrink>
									Persona
								</InputLabel>
								<OutlinedInput {...field} id={personaFieldId} notched label="Persona" placeholder="warm, unhurried" />
								{fieldState.error ? (
									<CustomFormHelperError text={fieldState.error.message || ""} />
								) : (
									<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
										How it reads to a listener. Shown next to the name.
									</Typography>
								)}
							</FormControl>
						)}
					/>
				</Grid>

				<Grid size={12}>
					<Controller
						control={control}
						name="notes"
						render={({ field, fieldState }) => (
							<TextField
								{...field}
								fullWidth
								multiline
								minRows={2}
								label="Our notes"
								placeholder="Where the sample came from, who approved it"
								error={Boolean(fieldState.error)}
								helperText={fieldState.error?.message ?? "Ours. Never shown to a client."}
							/>
						)}
					/>
				</Grid>

				{/* The sample: the half of a voice a client actually judges it on. Where it comes from
				    depends entirely on the provider, so this section is one act, not two greyed ones. */}
				{provider ? (
					<Grid size={12}>
						<Stack spacing={1.5}>
							<Typography variant="subtitle2">Sample</Typography>

							{isUpload ? (
								<Stack spacing={1} sx={{ alignItems: "flex-start" }}>
									<Typography variant="body2" color="text.secondary">
										This voice is a recording. Choose the audio file a client will hear.
									</Typography>

									<Button component="label" startIcon={<UploadSimpleIcon />} variant="outlined" size="small">
										{voice?.hasSample || sampleFile ? "Choose another file" : "Choose an audio file"}
										{/* Clipped rather than `hidden`: ButtonBase answers Enter and Space by
										    calling the `onClick` prop rather than clicking the element, and a
										    `display: none` input cannot be tabbed to — `hidden` leaves this
										    control mouse-only. */}
										<input
											type="file"
											style={visuallyHidden}
											accept={VOICE_SAMPLE_ACCEPT}
											onChange={(event) => {
												const file = event.target.files?.[0];
												// Cleared before the handler runs, so choosing the same file twice still fires.
												event.target.value = "";
												chooseFile(file);
											}}
										/>
									</Button>

									{sampleFile ? (
										<Typography variant="caption" color="text.secondary">
											{sampleFile.name} · {formatBytes(sampleFile.size)} — uploads when you {voice ? "save" : "add"} the
											voice.
										</Typography>
									) : (
										<Typography variant="caption" color="text.secondary">
											MP3, WAV, M4A, OGG, Opus, WebM or FLAC, up to {formatBytes(MAX_VOICE_SAMPLE_BYTES)}.
										</Typography>
									)}

									{fileError ? (
										<Alert severity="error" sx={{ width: "100%" }}>
											{fileError}
										</Alert>
									) : null}
								</Stack>
							) : (
								<Stack spacing={1}>
									<Controller
										control={control}
										name="sampleScript"
										render={({ field, fieldState }) => (
											<TextField
												{...field}
												fullWidth
												multiline
												minRows={2}
												label="Script"
												placeholder={DEFAULT_SAMPLE_SCRIPT}
												// Read-only rather than disabled: the point of watching a generation is to
												// re-read the line being spoken, and a disabled box leaves the tab order and
												// cannot be selected.
												slotProps={{ input: { readOnly: isGenerating } }}
												error={Boolean(fieldState.error)}
												helperText={
													fieldState.error?.message ??
													`What this voice reads when you generate a sample. ${script.trim().length}/${MAX_SAMPLE_SCRIPT_LENGTH}`
												}
											/>
										)}
									/>

									<Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
										<Button
											variant="outlined"
											size="small"
											disabled={isSaving || isGenerating || !script.trim() || !provider.canSynthesise}
											startIcon={isGenerating ? <CircularProgress size={16} /> : <WaveformIcon />}
											onClick={() => void generate()}
										>
											{isGenerating ? "Generating…" : voice?.hasSample ? "Generate again" : "Generate sample"}
										</Button>

										{isGenerating ? null : (
											<Typography variant="caption" color="text.secondary">
												{provider.canSynthesise
													? `Spoken by ${provider.title}, in this voice.`
													: `${provider.reason ?? "Not set up in this workspace"} — there is nothing here to speak it.`}
											</Typography>
										)}
									</Stack>

									{/* Synthesis is a websocket to a vendor and takes as long as it takes. An
									    indeterminate bar with the provider's name is what there is to say
									    honestly — a percentage here would be invented. */}
									{isGenerating ? (
										<Stack spacing={0.5}>
											<LinearProgress aria-label="Generating the sample" />
											<Typography variant="caption" color="text.secondary">
												{provider.title} is speaking the script. This can take up to a minute.
											</Typography>
										</Stack>
									) : null}

									{generateError ? (
										<Alert severity="error">
											{apiErrorMessage(generateError, "The sample could not be generated.")}
										</Alert>
									) : null}
								</Stack>
							)}

							{voice?.hasSample ? (
								<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
									{/* Keyed on the row's own timestamp: the sample URL never changes, so a
									    regenerated sample would otherwise play the bytes already cached
									    behind it. */}
									<VoiceSampleButton
										key={voice.updatedAt}
										sampleUrl={voice.sampleUrl}
										name={voice.displayName}
										disabled={isGenerating}
									/>
									<Typography variant="body2" color="text.secondary" sx={{ flex: "1 1 auto" }}>
										{voice.sampleSizeBytes ? formatBytes(voice.sampleSizeBytes) : "Sample recorded"}
									</Typography>
									<Button
										color="error"
										size="small"
										startIcon={<TrashIcon />}
										disabled={isGenerating}
										onClick={() => onRemoveSample(voice)}
									>
										Remove
									</Button>
								</Stack>
							) : null}
						</Stack>
					</Grid>
				) : null}

				<Grid size={12}>
					<Controller
						control={control}
						name="isActive"
						render={({ field }) => (
							<Stack spacing={0.5}>
								<FormControlLabel
									control={<Switch checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
									label="On the list"
								/>
								<Typography variant="caption" color="text.secondary">
									Every account is offered this voice while it is on. Give it a sample first — a client chooses by
									listening.
								</Typography>
							</Stack>
						)}
					/>
				</Grid>
			</Grid>
		</CustomDrawer>
	);
}
